import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
  type IndexPaginationMeta,
} from '@/components/tables/IndexTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { StyledIcon } from '@/components/ui/styled-icon'
import { EmptyState } from '@/components/patterns/empty-state'
import { LoadingState } from '@/components/patterns/loading-state'
import dayjs from '@/lib/dayjs'
import type { ReportDateRange } from '@/lib/report-date-range'
import {
  getPhoneClickGranularityOptionsForRange,
  PHONE_CLICK_REPORT_SORT,
  type PhoneClickGranularity,
} from '@/lib/services/reports/reports.schema'
import type {
  PhoneClickReport,
  PhoneClickReportSort,
  PhoneClickReportTimeSeriesPoint,
} from '@/lib/services/reports/reports.types'
import { readProperty } from '@/lib/utils'
import type { Dayjs } from 'dayjs'
import { Info, MousePointerClick, PhoneCall } from 'lucide-react'
import { Area, Bar, CartesianGrid, ComposedChart, XAxis, YAxis } from 'recharts'
import { phoneClickColumns } from './-table-columns'

const BUCKETED_CLICKS_COLOR = 'var(--color-chart-2)'
const CUMULATIVE_CLICKS_KEY = 'cumulative_clicks_count'

/** A page or page-size change from the details table's pagination. */
type PhoneClickReportPageChange = {
  page: number
  page_size?: number
}

type PhoneClickReportResultsProps = {
  report: PhoneClickReport | undefined
  /** The requested range, which sets the group-by options. */
  selectedRange: ReportDateRange
  granularity?: PhoneClickGranularity
  /** Receives the group-by select's value. */
  onGranularityChange?: (granularity: string) => void
  onPaginationChange?: (change: PhoneClickReportPageChange) => void
  sort?: PhoneClickReportSort
  onSortChange?: (sort: string) => void
  isFetching?: boolean
}

/** Clicks summed over one day, week or month of the time series. */
type ClickBucket = {
  /** `Y-m-d`: the first and last days in the series that fall in this bucket. */
  bucket_start: string
  bucket_end: string
  clicks_count: number
}

export function PhoneClickReportResults({
  report,
  selectedRange,
  granularity = 'daily',
  onGranularityChange,
  onPaginationChange,
  sort = PHONE_CLICK_REPORT_SORT.DATE_DESC,
  onSortChange,
  isFetching = false,
}: PhoneClickReportResultsProps) {
  if (!report) {
    return <LoadingState label="Loading report…" />
  }

  const totalClicks = report?.summary?.total_clicks ?? 0
  const timeSeries = report?.time_series ?? []
  const timezone = report.filters.timezone
  const bucketLabel = getBucketLabel(granularity)
  const bucketedClicksLabel = `${bucketLabel} clicks/taps`
  const chartData = buildCumulativeClickData(timeSeries, granularity)
  const granularityOptions = getPhoneClickGranularityOptionsForRange(selectedRange)
  const chartConfig = {
    clicks_count: {
      label: bucketedClicksLabel,
      color: BUCKETED_CLICKS_COLOR,
    },
    [CUMULATIVE_CLICKS_KEY]: {
      label: 'Cumulative in selected range',
      color: 'var(--color-chart-1)',
    },
  }
  const rows = report?.rows ?? []
  const meta = getIndexPaginationMeta(report, 1, 7)

  return (
    <>
      <div className="grid min-w-0 gap-4 @4xl:grid-cols-[minmax(14rem,1fr)_minmax(0,2fr)]">
        <Card className="min-w-0">
          <CardContent className="flex min-h-full min-w-0 items-center justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              <CardDescription>Phone link clicks/taps</CardDescription>
              <Typography as="p" variant="h1" className="tabular-nums">
                {totalClicks.toLocaleString()}
              </Typography>
            </div>
            <StyledIcon
              icon={MousePointerClick}
              tone="brand"
              className="size-12"
              iconClassName="size-6"
            />
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardContent className="flex min-h-full min-w-0 items-center gap-4">
            <StyledIcon icon={Info} tone="neutral" />
            <div className="min-w-0">
              <Typography variant="label" as="p">
                What these clicks/taps mean
              </Typography>
              <Typography variant="description" className="mt-1.5 max-w-2xl">
                Clicking/tapping your phone number shows interest in calling, but doesn't confirm
                that a call was made or an order was placed. Rapid clicking only counts once.
              </Typography>
            </div>
          </CardContent>
        </Card>
      </div>

      {totalClicks > 0 ? (
        <Card className="min-w-0">
          <CardHeader className="gap-4 @2xl:flex @2xl:flex-row @2xl:items-start @2xl:justify-between">
            <div className="min-w-0 space-y-1.5">
              <CardTitle>Clicks/taps over time</CardTitle>
              <CardDescription>
                Cumulative clicks/taps in the selected range with {bucketLabel.toLowerCase()}{' '}
                clicks/taps underneath
              </CardDescription>
            </div>
            {onGranularityChange ? (
              <div className="flex shrink-0 items-center gap-2">
                <Typography variant="label" tone="muted">
                  Group by
                </Typography>
                <Select value={granularity} onValueChange={onGranularityChange}>
                  <SelectTrigger size="sm" className="w-[112px]" aria-label="Group by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    {granularityOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        <span className="flex flex-col items-start">
                          <span>{option.label}</span>
                          {option.disabled && option.unavailableLabel ? (
                            <Typography variant="caption">
                              Up to {option.unavailableLabel}
                            </Typography>
                          ) : null}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="min-w-0">
            <div className="flex min-w-0 flex-col gap-3">
              <Typography
                as="div"
                variant="caption"
                className="flex flex-wrap items-center justify-between gap-2"
                aria-label="Chart series"
              >
                <ChartSeriesLabel color={BUCKETED_CLICKS_COLOR} label={bucketedClicksLabel} />
                <ChartSeriesLabel
                  color="var(--color-chart-1)"
                  label="Cumulative in selected range"
                />
              </Typography>
              <ChartContainer
                config={chartConfig}
                className="h-[250px] w-full min-w-0 max-w-full aspect-auto"
              >
                <ComposedChart
                  accessibilityLayer
                  data={chartData}
                  margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="phone-clicks-cumulative-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={`var(--color-${CUMULATIVE_CLICKS_KEY})`}
                        stopOpacity={0.26}
                      />
                      <stop
                        offset="95%"
                        stopColor={`var(--color-${CUMULATIVE_CLICKS_KEY})`}
                        stopOpacity={0.03}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid yAxisId="bucketed" vertical={false} />
                  <XAxis
                    dataKey="bucket_start"
                    axisLine={false}
                    tickLine={false}
                    minTickGap={24}
                    tickMargin={8}
                    tickFormatter={(date) => formatBucketAxisLabel(date, granularity, timezone)}
                  />
                  <YAxis
                    yAxisId="bucketed"
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={8}
                    width={32}
                  />
                  <YAxis
                    yAxisId="cumulative"
                    orientation="right"
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={8}
                    width={42}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_label, payload) =>
                          formatBucketTooltipLabel(payload?.[0]?.payload, granularity, timezone)
                        }
                      />
                    }
                  />
                  <Bar
                    yAxisId="bucketed"
                    dataKey="clicks_count"
                    fill="var(--color-clicks_count)"
                    fillOpacity={0.86}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    isAnimationActive={false}
                  />
                  <Area
                    yAxisId="cumulative"
                    dataKey={CUMULATIVE_CLICKS_KEY}
                    type="monotone"
                    stroke={`var(--color-${CUMULATIVE_CLICKS_KEY})`}
                    fill="url(#phone-clicks-cumulative-fill)"
                    strokeWidth={2.5}
                    dot={chartData.length === 1 ? { r: 4 } : false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>
      ) : (
        <EmptyPhoneClickState />
      )}

      {meta.total > 0 || meta.current_page > 1 ? (
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Click/tap details</CardTitle>
            <CardDescription>Daily clicks/taps grouped by phone number</CardDescription>
          </CardHeader>
          <CardContent className="min-w-0">
            <IndexDataTable
              mode="server"
              data={rows}
              columns={phoneClickColumns}
              sort={sort}
              onSortChange={onSortChange}
              getRowId={(row) => `${row.date}:${row.phone_number}`}
              isLoading={isFetching}
            />
            <IndexTablePagination
              meta={meta}
              pageSize={meta.page_size}
              pageSizeOptions={[7, 10, 20, 50]}
              onPageChange={(page) => onPaginationChange?.({ page })}
              onPageSizeChange={(pageSize) =>
                onPaginationChange?.({ page: 1, page_size: pageSize })
              }
              summary={formatPhoneClickPaginationSummary(meta)}
            />
          </CardContent>
        </Card>
      ) : null}
    </>
  )
}

function formatPhoneClickPaginationSummary(meta: IndexPaginationMeta) {
  const firstEntry = (meta.current_page - 1) * meta.page_size + 1
  const lastEntry = Math.min(meta.current_page * meta.page_size, meta.total)

  return `Showing ${firstEntry.toLocaleString()} to ${lastEntry.toLocaleString()} of ${meta.total.toLocaleString()} entries`
}

function buildCumulativeClickData(
  timeSeries: PhoneClickReportTimeSeriesPoint[],
  granularity: PhoneClickGranularity
) {
  const buckets = buildClickBuckets(timeSeries, granularity)
  let cumulativeClicks = 0

  return buckets.map((bucket) => {
    cumulativeClicks += Number(bucket.clicks_count)

    return {
      ...bucket,
      [CUMULATIVE_CLICKS_KEY]: cumulativeClicks,
    }
  })
}

function buildClickBuckets(
  timeSeries: PhoneClickReportTimeSeriesPoint[],
  granularity: PhoneClickGranularity
) {
  const buckets = new Map<string, ClickBucket>()
  const sortedDays = [...timeSeries].sort((left, right) =>
    String(left?.date ?? '').localeCompare(String(right?.date ?? ''))
  )

  sortedDays.forEach((day) => {
    const date = parseDate(day?.date)

    if (!date.isValid()) {
      return
    }

    const bucketKey = getBucketKey(date, granularity)
    const existingBucket = buckets.get(bucketKey)

    if (existingBucket) {
      existingBucket.bucket_end = day.date
      existingBucket.clicks_count += Number(day?.clicks_count ?? 0)
      return
    }

    buckets.set(bucketKey, {
      bucket_start: day.date,
      bucket_end: day.date,
      clicks_count: Number(day?.clicks_count ?? 0),
    })
  })

  return [...buckets.values()]
}

function getBucketKey(date: Dayjs, granularity: PhoneClickGranularity) {
  if (granularity === 'monthly') {
    return date.format('YYYY-MM')
  }

  if (granularity === 'weekly') {
    return date.subtract(date.day(), 'day').format('YYYY-MM-DD')
  }

  return date.format('YYYY-MM-DD')
}

function parseDate(value: string | undefined, timezone?: string) {
  return timezone ? dayjs.tz(value, timezone) : dayjs(value)
}

function getBucketLabel(granularity: PhoneClickGranularity) {
  return (
    {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
    }[granularity] ?? 'Period'
  )
}

// Recharts types tick values as `any`; the ticks are the `bucket_start` dates.
function formatBucketAxisLabel(
  value: unknown,
  granularity: PhoneClickGranularity,
  timezone: string
) {
  if (typeof value !== 'string') {
    return ''
  }

  const parsed = parseDate(value, timezone)

  if (!parsed.isValid()) {
    return value
  }

  return parsed.format(granularity === 'monthly' ? 'MMM' : 'MMM D')
}

// Recharts types the hovered data row as `any`; it is a ClickBucket.
function formatBucketTooltipLabel(
  bucket: unknown,
  granularity: PhoneClickGranularity,
  timezone: string
) {
  const start = parseDate(readBucketDate(bucket, 'bucket_start'), timezone)
  const end = parseDate(readBucketDate(bucket, 'bucket_end'), timezone)

  if (!start.isValid()) {
    return ''
  }

  if (granularity === 'monthly') {
    return start.format('MMMM YYYY')
  }

  if (granularity === 'weekly' && end.isValid()) {
    return start.isSame(end, 'month')
      ? `${start.format('MMM D')} - ${end.format('D')}`
      : `${start.format('MMM D')} - ${end.format('MMM D')}`
  }

  return start.format('ddd MMM D, YYYY')
}

function readBucketDate(bucket: unknown, key: 'bucket_start' | 'bucket_end') {
  const value = readProperty(bucket, key)

  return typeof value === 'string' ? value : undefined
}

function ChartSeriesLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {label}
    </span>
  )
}

function EmptyPhoneClickState() {
  return (
    <EmptyState
      icon={PhoneCall}
      title="No phone link clicks/taps in the selected date range"
      description="Try a wider date range to look for earlier website activity."
      surface="card"
      className="min-w-0"
    />
  )
}
